import { useState, useEffect, useMemo, useCallback } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, CalendarDays, Car, Users, Palmtree,
  Settings, LogOut, Menu, X, Bell, ChevronRight, User, UserCog, Megaphone,
  Building2, Globe, Mail, Briefcase, Download, Network, Activity,
  MessageSquare, Tag, Link2, MailCheck, FileText, Code2, ClipboardList,
  Ticket, Send, BookOpen, BarChart3, FileCheck, RefreshCw, FileArchive, ShieldAlert, ShieldCheck, Shield, Database, Wrench,
  Search, Star, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import RequireModule from "@/components/RequireModule";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { KeyRound } from "lucide-react";

// Iter 157 — 7 grupos lógicos. NUNCA hardcodear roles aquí: el filtrado real
// se hace vía `hasPermission(module, "ver")` con el módulo del backend.
// `keywords`: sinónimos para el buscador (en inglés/español, sin tildes).
const adminNavGroups = [
  {
    label: "Operación diaria",
    items: [
      { path: "/admin", label: "Dashboard", icon: LayoutDashboard, module: "dashboard", keywords: ["inicio", "panel", "home"] },
      { path: "/admin/citas", label: "Citas", icon: CalendarDays, module: "citas", keywords: ["cita", "agenda", "reserva"] },
      { path: "/admin/vehiculos", label: "Vehículos", icon: Car, module: "vehiculos", keywords: ["vehiculo", "coches", "flota"] },
      { path: "/admin/comunicaciones?tab=whatsapp", label: "WhatsApp", icon: MessageSquare, module: "whatsapp", keywords: ["mensaje", "wa"] },
    ],
  },
  {
    label: "Citas y taller",
    items: [
      { path: "/admin/citas/servicios", label: "Servicios de cita", icon: Wrench, module: "citas", keywords: ["servicio", "cita", "taller"] },
      { path: "/admin/vehiculos-alquiler", label: "Vehículos de alquiler", icon: Car, module: "citas", keywords: ["alquiler", "rental", "flota", "vehiculo"] },
      { path: "/admin/vehiculos-alquiler?tab=contacts", label: "Contactos de alquiler", icon: UserCog, module: "citas", keywords: ["alquiler", "contacto", "persona"] },
    ],
  },
  {
    label: "Atenciones comerciales",
    items: [
      { path: "/admin/atenciones", label: "Dashboard", icon: Ticket, module: "atenciones_comerciales", keywords: ["atenciones", "comercial", "panel"] },
      { path: "/admin/atenciones/bonos", label: "Bonos emitidos", icon: FileCheck, module: "atenciones_comerciales", keywords: ["bono", "bonos", "vale", "atencion"] },
      { path: "/admin/atenciones/emitir", label: "Emitir bono", icon: Send, module: "atenciones_comerciales", keywords: ["bono", "bonos", "emitir", "crear", "vale"] },
      { path: "/admin/atenciones/canjes", label: "Canjes", icon: BarChart3, module: "atenciones_comerciales", keywords: ["canje", "bono", "bonos", "atencion", "redencion"] },
      { path: "/admin/atenciones/informes", label: "Informes", icon: BarChart3, module: "atenciones_comerciales", keywords: ["informe", "reporte", "atencion", "bonos"] },
      { path: "/admin/atenciones/plantillas", label: "Plantillas", icon: BookOpen, module: "atenciones_comerciales", keywords: ["plantilla", "template", "bonos"] },
      { path: "/admin/atenciones/config", label: "Configuración", icon: Settings, module: "atenciones_comerciales", keywords: ["config", "ajustes", "atenciones"] },
    ],
  },
  {
    label: "Clientes y comunicación",
    items: [
      { path: "/admin/clientes", label: "Ficha de cliente", icon: User, module: "clientes", keywords: ["cliente", "ficha", "360", "vista"] },
      { path: "/admin/comunicaciones", label: "Centro de Comunicaciones", icon: MailCheck, module: "email_config", keywords: ["email", "correo", "mail", "comunicacion"] },
      { path: "/admin/consultas", label: "Consultas Contacto", icon: FileText, module: "consultas", keywords: ["consulta", "contacto", "formulario"] },
      { path: "/admin/candidaturas", label: "Candidaturas", icon: Briefcase, module: "candidaturas", keywords: ["candidato", "trabajo", "rrhh", "empleos"] },
      { path: "/admin/leads-historicos", label: "Leads históricos", icon: Database, module: "leads_historicos", keywords: ["leads", "historico", "cliente"] },
      // Iter 207 — Avisos web públicos
      { path: "/admin/comunicaciones/avisos-web", label: "Avisos web", icon: Megaphone, module: "avisos_web", keywords: ["avisos", "banner", "comunicacion", "public", "horario", "vacaciones"] },
      { path: "/admin/ctas-enlaces", label: "CTAs y enlaces", icon: Link2, module: "contenido_web", keywords: ["cta", "enlace", "link", "boton"] },
    ],
  },
  {
    label: "Comercial y web",
    items: [
      { path: "/admin/contenido-web", label: "Contenido Web", icon: Globe, module: "contenido_web", keywords: ["web", "contenido", "cms", "pagina"] },
      { path: "/admin/promociones", label: "Promociones", icon: Tag, module: "promotions", keywords: ["promocion", "oferta", "descuento"] },
      { path: "/admin/formularios", label: "Formularios públicos", icon: FileText, module: "formularios", keywords: ["formulario", "form", "publico"] },
      { path: "/admin/gtm", label: "Google Tag Manager", icon: Code2, module: "gtm", keywords: ["gtm", "tag", "manager", "analytics"] },
    ],
  },
  {
    label: "Administración interna",
    items: [
      { path: "/admin/centros", label: "Centros", icon: Building2, module: "centros", keywords: ["centro", "sede", "taller"] },
      { path: "/admin/vendedores", label: "Vendedores", icon: Users, module: "centros", keywords: ["vendedor", "comercial"] },
      { path: "/admin/empleados", label: "Empleados", icon: Users, module: "empleados", keywords: ["empleado", "empleados", "personal", "trabajador"] },
      { path: "/admin/rrhh", label: "RRHH", icon: Briefcase, module: "rrhh", keywords: ["rrhh", "recursos humanos", "empleado", "empleados", "personal"] },
      { path: "/admin/catalogos-rrhh", label: "Catálogos RRHH", icon: ClipboardList, module: "catalogos_rrhh", keywords: ["catalogo", "rrhh", "empleado", "empleados"] },
      { path: "/admin/rrhh/configuracion", label: "Configuración RRHH", icon: Settings, module: "rrhh", keywords: ["config", "rrhh", "ajustes"] },
      { path: "/admin/vacaciones", label: "Vacaciones", icon: Palmtree, module: "vacaciones", keywords: ["vacacion", "ausencia", "permiso"] },
      { path: "/admin/planificacion-ausencias", label: "Planificación ausencias", icon: Palmtree, module: "vacaciones", keywords: ["planificacion", "calendario", "ausencias", "rrhh", "vacaciones", "permisos"] },
      { path: "/admin/organigrama", label: "Organigrama", icon: Network, module: "organigrama", keywords: ["organigrama", "estructura", "empleado", "empleados", "jerarquia"] },
    ],
  },
  {
    label: "Sistema y seguridad",
    items: [
      // Iter 189 (P0) — Nueva entrada unificada
      { path: "/admin/accesos-permisos", label: "Accesos y permisos", icon: ShieldCheck, module: "accesos_permisos", keywords: ["accesos", "permisos", "roles", "usuarios", "rbac", "seguridad", "matriz"] },
      // Iter 199 (P0) — Entrada propia para Usuarios del sistema (NO confundir
      // con la pestaña "Usuarios" dentro de /admin/accesos-permisos).
      { path: "/admin/usuarios", label: "Usuarios del sistema", icon: UserCog, module: "usuarios_sistema", keywords: ["usuarios", "cuentas", "logins", "altas", "sistema"] },
      // Iter 189 (P0) — Las 3 entradas antiguas quedan ocultas del menú pero
      // accesibles por URL directa durante 30 días (compat):
      //   /admin/permisos-y-roles, /admin/usuarios-permisos
      { path: "/admin/auditoria-permisos", label: "Auditoría Permisos y Datos", icon: ShieldCheck, module: "auditoria_permisos", superAdminOnly: true, keywords: ["auditoria", "permisos", "datos", "audit"] },
      { path: "/admin/diagnostico-permisos", label: "Diagnóstico Permisos", icon: ShieldAlert, module: "diagnostico_permisos", keywords: ["diagnostico", "permisos", "debug"] },
      { path: "/admin/salud-datos", label: "Salud de Datos", icon: Activity, module: "salud_datos", keywords: ["salud", "datos", "health", "calidad"] },
      { path: "/admin/copias-seguridad", label: "Copias de Seguridad", icon: FileArchive, module: "copias_seguridad", keywords: ["backup", "copia", "seguridad", "respaldo", "backups"] },
      { path: "/admin/informes-automaticos", label: "Informes automáticos", icon: Mail, module: "informes_automaticos", keywords: ["informe", "reporte", "diario", "email", "estado", "report", "monitor"] },
      { path: "/admin/sincronizar-contenido", label: "Sincronizar Contenido", icon: RefreshCw, module: "sincronizar_contenido", keywords: ["sync", "sincronizar", "contenido", "replicacion"] },
      { path: "/admin/exportacion", label: "Exportación", icon: Download, module: "export", keywords: ["export", "exportacion", "csv", "descargar"] },
      { path: "/admin/seguridad/eventos", label: "Registro de Seguridad", icon: Shield, module: "seguridad", keywords: ["seguridad", "registro", "eventos", "log", "security"] },
      { path: "/admin/configuracion", label: "Configuración General", icon: Settings, module: "configuracion", keywords: ["config", "ajustes", "general", "settings"] },
    ],
  },
];

// Flatten for mobile and other uses
const adminNav = adminNavGroups.flatMap(g => g.items);

const employeeNav = [
  { path: "/empleado", label: "Mi Panel", icon: LayoutDashboard },
  { path: "/empleado/vacaciones", label: "Vacaciones", icon: Palmtree },
  { path: "/empleado/calendario", label: "Mi calendario", icon: Palmtree },
];

export default function AdminLayout({ isEmployee = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [companyName, setCompanyName] = useState("");
  // Iter 188 (P1) — contador de "Pendientes mías" (jefe directo + gerente)
  const [pendingForMe, setPendingForMe] = useState(0);
  // Iter 189 Fase 2 — módulos visibles del registro central (filtra sidebar)
  const [allowedKeys, setAllowedKeys] = useState(null); // null = aún cargando; Set = ya cargado
  const { user, logout, axiosAuth, hasPermission, isSuperAdmin, permissionsLoaded } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Filtra ítems del menú según permisos reales. super_admin ve todo.
  // El nivel del rol NUNCA concede acceso automático.
  const canShow = (item) => {
    if (isEmployee) return true;
    if (item.superAdminOnly) return isSuperAdmin;
    if (isSuperAdmin) return true;
    if (!item.module) return false;
    // Iter 189 Fase 2 — Si tenemos lista de módulos efectivos del registro central,
    // la usamos como fuente de verdad. Fallback a hasPermission.
    if (allowedKeys && allowedKeys.size > 0) {
      return allowedKeys.has(item.module);
    }
    return hasPermission(item.module, "ver");
  };
  const filteredGroups = isEmployee
    ? []
    : adminNavGroups
        .map(g => ({ ...g, items: g.items.filter(canShow) }))
        .filter(g => g.items.length > 0);
  const navItems = isEmployee
    ? employeeNav
    : filteredGroups.flatMap(g => g.items);

  // ─── Iter 157 — Sidebar state: collapsed groups, favoritos, búsqueda ───────
  const navStorageKey = `admin:nav:${user?.id || "anon"}`;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(`${navStorageKey}:collapsed`);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(`${navStorageKey}:favorites`);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [navSearch, setNavSearch] = useState("");

  const toggleGroup = useCallback((label) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [label]: !prev[label] };
      try { localStorage.setItem(`${navStorageKey}:collapsed`, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [navStorageKey]);

  const toggleFavorite = useCallback((path) => {
    setFavorites(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      try { localStorage.setItem(`${navStorageKey}:favorites`, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [navStorageKey]);

  // Persist favorites/collapsed cuando cambia user (al iniciar sesión con otra cuenta)
  useEffect(() => {
    if (!user?.id) return;
    try {
      setCollapsedGroups(JSON.parse(localStorage.getItem(`admin:nav:${user.id}:collapsed`) || "{}"));
      setFavorites(JSON.parse(localStorage.getItem(`admin:nav:${user.id}:favorites`) || "[]"));
    } catch {}
  }, [user?.id]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`).then(r => r.json()).then(d => setCompanyName(d.company_name || "")).catch(() => {});
  }, []);

  // Iter 189 Fase 2 — Cargar módulos efectivos para filtrar el sidebar
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axiosAuth().get("/system/modules/effective");
        if (!cancel) {
          const keys = new Set((res.data?.modules || []).map((m) => m.key));
          setAllowedKeys(keys);
        }
      } catch {
        if (!cancel) setAllowedKeys(new Set());
      }
    })();
    return () => { cancel = true; };
  }, [axiosAuth]);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        // Iter 133: modal rápido = solo no leídas, limit 10
        axiosAuth().get("/notifications?only_unread=true&limit=10"),
        axiosAuth().get("/notifications/unread-count"),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch { /* network error — ignorar, próximo tick lo intentará */ }
  };

  // Iter 188 (P1) — Contador "Pendientes mías" para badge sidebar
  const fetchPendingForMe = useCallback(async () => {
    if (isEmployee) return;
    try {
      const res = await axiosAuth().get("/vacation-requests/pending-for-me");
      const totals = res.data?.totals || { direct: 0, manager: 0 };
      setPendingForMe((totals.direct || 0) + (totals.manager || 0));
    } catch {
      // silent — usuario sin permisos o sin pendientes
    }
  }, [axiosAuth, isEmployee]);

  useEffect(() => {
    fetchNotifications();
    fetchPendingForMe();
    const interval = setInterval(() => {
      fetchNotifications();
      fetchPendingForMe();
    }, 30000);
    // Refresh inmediato cuando la pestaña "Pendientes mías" dispara un cambio
    const onChange = () => fetchPendingForMe();
    window.addEventListener("pending-approval-changed", onChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener("pending-approval-changed", onChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAllRead = async () => {
    try {
      await axiosAuth().post("/notifications/read-all");
      setUnreadCount(0);
      setNotifications([]);  // modal solo mostraba no leídas → ahora vacío
    } catch { /* ignorar */ }
  };

  // Iter 133: click sobre notificación → marcar leída, cerrar modal, navegar
  const handleNotificationClick = async (n) => {
    try {
      await axiosAuth().post(`/notifications/${n.id}/read`);
    } catch { /* permitimos navegar igual */ }
    // Quitar localmente del modal
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    setUnreadCount(c => Math.max(0, c - 1));
    setNotifOpen(false);
    const url = n.action_url || n.link || "/admin";
    if (url.startsWith("http")) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Iter 157 fix — Determinar el ítem activo con LONGEST-PREFIX MATCH.
  // Antes: cada item evaluaba `pathname.startsWith(path)` y, por ejemplo,
  // /admin/citas/servicios marcaba ACTIVO también /admin/citas (Operación diaria).
  // Ahora: elegimos el item cuyo path es el prefijo más largo de la URL actual,
  // y `isActive(path)` compara exactamente con el ganador.
  const activeItemPath = useMemo(() => {
    const all = [
      ...adminNavGroups.flatMap(g => g.items),
      ...employeeNav,
    ];
    const currentTab = new URLSearchParams(location.search).get("tab") || "";
    const currentPath = location.pathname;
    let best = null;
    let bestLen = -1;
    for (const it of all) {
      if (!it.path) continue;
      let p = it.path;
      let linkTab = "";
      if (p.includes("?")) {
        const [pp, qq] = p.split("?");
        p = pp;
        linkTab = new URLSearchParams(qq).get("tab") || "";
      }
      // Si el path tiene tab, debe coincidir exactamente con la tab actual.
      // Si no tiene tab, coincide cuando la URL tampoco tiene tab "especial"
      // o cuando se trata del path padre.
      const samePath = currentPath === p || currentPath.startsWith(p + "/");
      if (!samePath) continue;
      // Path raíz solo activo si es exacto:
      if ((p === "/admin" || p === "/empleado") && currentPath !== p) continue;
      // Caso especial: comunicaciones sin tab ≠ comunicaciones?tab=whatsapp
      if (p === "/admin/comunicaciones") {
        if (linkTab && currentTab !== linkTab) continue;
        if (!linkTab && currentTab === "whatsapp") continue;
      } else if (linkTab && linkTab !== currentTab) {
        continue;
      }
      // Longest path wins. Si misma longitud, prioriza coincidencia con tab.
      const score = p.length + (linkTab && linkTab === currentTab ? 0.5 : 0);
      if (score > bestLen) {
        bestLen = score;
        best = it.path;
      }
    }
    return best;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const isActive = (path) => path === activeItemPath;

  // ─── Iter 157 — derivados de búsqueda, favoritos, breadcrumb, grupo activo ─
  // Normaliza para búsqueda: sin tildes, lowercase, plural→singular básico
  const normalize = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  const stem = (s) => {
    let x = normalize(s);
    // plural→singular muy simple: quita 's' final si no son palabras de 2 letras
    if (x.length > 3 && x.endsWith("es")) x = x.slice(0, -2);
    else if (x.length > 2 && x.endsWith("s")) x = x.slice(0, -1);
    return x;
  };

  const searchedGroups = useMemo(() => {
    if (!navSearch.trim()) return filteredGroups;
    const q = stem(navSearch);
    return filteredGroups
      .map(g => ({
        ...g,
        items: g.items.filter(it => {
          const haystacks = [
            normalize(it.label),
            stem(it.label),
            normalize(g.label),
            ...(it.keywords || []).map(k => normalize(k)),
            ...(it.keywords || []).map(k => stem(k)),
          ];
          return haystacks.some(h => h.includes(q));
        }),
      }))
      .filter(g => g.items.length > 0);
  }, [filteredGroups, navSearch]);

  const allVisibleItems = useMemo(
    () => filteredGroups.flatMap(g => g.items),
    [filteredGroups],
  );

  const favoriteItems = useMemo(
    () => favorites.map(p => allVisibleItems.find(it => it.path === p)).filter(Boolean),
    [favorites, allVisibleItems],
  );

  // grupo activo según ruta actual: contiene el ítem activo
  const activeGroupLabel = useMemo(() => {
    for (const g of filteredGroups) {
      if (g.items.some(it => isActive(it.path))) return g.label;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroups, location.pathname, location.search]);

  // Auto-expandir grupo activo: si está colapsado, lo abrimos cuando cambia la ruta.
  useEffect(() => {
    if (activeGroupLabel && collapsedGroups[activeGroupLabel]) {
      setCollapsedGroups(prev => {
        const next = { ...prev, [activeGroupLabel]: false };
        try { localStorage.setItem(`${navStorageKey}:collapsed`, JSON.stringify(next)); } catch {}
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupLabel]);

  // Breadcrumb: "Grupo > Ítem activo"
  const breadcrumb = useMemo(() => {
    for (const g of adminNavGroups) {
      for (const it of g.items) {
        if (isActive(it.path)) return { group: g.label, item: it.label };
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // ── helpers de render reutilizables ───────────────────────────────────────
  const navLinkClass = (active) =>
    `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
      active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`;
  const slugTestId = (label) =>
    label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s/g, '-');

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full z-40">
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
            <div className="w-8 h-8 bg-red-600 rounded-sm flex items-center justify-center">
              <span className="text-white font-bold text-xs font-['Barlow_Condensed']">AS</span>
            </div>
            <div>
              <span className="heading-industrial text-lg font-bold text-slate-900 block leading-none">{companyName || "PANEL"}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">
                {isEmployee ? "Portal Empleado" : "Panel Admin"}
              </span>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin" data-testid="admin-sidebar-nav">
          {!isEmployee && (
            // Buscador
            <div className="px-1 pb-2 sticky top-0 bg-white z-10">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  data-testid="admin-nav-search"
                  value={navSearch}
                  onChange={(e) => setNavSearch(e.target.value)}
                  placeholder="Buscar módulo..."
                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-slate-50"
                />
                {navSearch && (
                  <button type="button" onClick={() => setNavSearch("")}
                    data-testid="admin-nav-search-clear"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded">
                    <X className="w-3 h-3 text-slate-400" />
                  </button>
                )}
              </div>
            </div>
          )}
          {isEmployee ? (
            navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={navLinkClass(isActive(item.path))}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))
          ) : (
            <>
              {/* Mis accesos (favoritos) */}
              {!navSearch && favoriteItems.length > 0 && (
                <div className="mb-3" data-testid="nav-favorites-section">
                  <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-amber-600 px-3 pt-2 pb-1.5 select-none flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" /> Mis accesos
                  </p>
                  {favoriteItems.map((item) => (
                    <div key={`fav-${item.path}`} className="group relative">
                      <Link
                        to={item.path}
                        data-testid={`nav-fav-${slugTestId(item.label)}`}
                        className={navLinkClass(isActive(item.path))}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="flex-1 truncate">{item.label}</span>
                      </Link>
                      <button type="button" onClick={() => toggleFavorite(item.path)}
                        data-testid={`nav-fav-toggle-${slugTestId(item.label)}`}
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-slate-200/60 rounded"
                        title="Quitar de favoritos">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-500" />
                      </button>
                    </div>
                  ))}
                  <div className="h-px bg-slate-100 mt-3 mb-1" />
                </div>
              )}

              {/* Grupos */}
              {searchedGroups.map((group) => {
                const isCollapsed = !navSearch && !!collapsedGroups[group.label];
                const isGroupActive = group.label === activeGroupLabel;
                return (
                  <div key={group.label} className="mb-2" data-testid={`nav-group-${slugTestId(group.label)}`}>
                    <button
                      type="button"
                      onClick={() => !navSearch && toggleGroup(group.label)}
                      data-testid={`nav-group-toggle-${slugTestId(group.label)}`}
                      className={`w-full flex items-center gap-1.5 px-3 pt-3 pb-1.5 text-[9px] uppercase tracking-[0.15em] font-bold select-none transition-colors ${
                        isGroupActive ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'
                      }`}
                      disabled={!!navSearch}
                    >
                      <ChevronDown
                        className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                      />
                      {group.label}
                    </button>
                    {!isCollapsed && group.items.map((item) => {
                      // Iter 188 (P1) — Badge "pendientes mías" en RRHH
                      const showPendingBadge = item.path === "/admin/rrhh" && pendingForMe > 0;
                      return (
                      <div key={item.path} className="group relative">
                        <Link
                          to={showPendingBadge ? "/admin/rrhh?tab=pendientes-mias" : item.path}
                          data-testid={`nav-${slugTestId(item.label)}`}
                          className={navLinkClass(isActive(item.path))}
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="flex-1 truncate">{item.label}</span>
                          {showPendingBadge && (
                            <span
                              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full"
                              data-testid="nav-pending-badge-rrhh"
                              title={`Tienes ${pendingForMe} solicitud${pendingForMe === 1 ? "" : "es"} pendiente${pendingForMe === 1 ? "" : "s"} de aprobar`}
                            >
                              {pendingForMe}
                            </span>
                          )}
                        </Link>
                        <button type="button" onClick={() => toggleFavorite(item.path)}
                          data-testid={`nav-fav-toggle-${slugTestId(item.label)}`}
                          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-slate-200/60 rounded"
                          title={favorites.includes(item.path) ? "Quitar de favoritos" : "Marcar como favorito"}>
                          <Star className={`w-3 h-3 ${favorites.includes(item.path) ? 'fill-amber-400 text-amber-500' : 'text-slate-400'}`} />
                        </button>
                      </div>
                    );
                    })}
                  </div>
                );
              })}
              {!isEmployee && searchedGroups.length === 0 && navSearch && (
                <p className="text-xs text-slate-400 px-3 py-4 text-center" data-testid="nav-search-empty">
                  Sin resultados para "{navSearch}"
                </p>
              )}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center px-4 lg:px-6">
          <button className="lg:hidden mr-3 p-2 rounded-sm hover:bg-slate-100 transition-colors" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-toggle">
            {sidebarOpen ? <X className="w-5 h-5 text-slate-900" /> : <Menu className="w-5 h-5 text-slate-900" />}
          </button>
          <span className="lg:hidden text-sm font-semibold heading-industrial uppercase text-slate-900">
            {isEmployee ? "Portal Empleado" : "Panel Admin"}
          </span>

          {/* Iter 157 — Breadcrumb */}
          {!isEmployee && breadcrumb && (
            <nav className="hidden lg:flex items-center gap-1.5 text-xs text-slate-500" aria-label="breadcrumb"
              data-testid="admin-breadcrumb">
              <span className="text-slate-400">{breadcrumb.group}</span>
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <span className="font-semibold text-slate-700" data-testid="admin-breadcrumb-current">{breadcrumb.item}</span>
            </nav>
          )}

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            {!isEmployee && user?.employee_id && (
              <Link
                to="/empleado"
                data-testid="goto-employee-portal-btn"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 py-1.5 rounded-sm transition-colors uppercase tracking-wider"
              >
                <User className="w-3.5 h-3.5" /> Mi Portal
              </Link>
            )}
            {isEmployee && user?.role && !["employee", "empleado"].includes(user.role) && (
              <Link
                to="/admin"
                data-testid="goto-admin-panel-btn"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 py-1.5 rounded-sm transition-colors uppercase tracking-wider"
              >
                <LayoutDashboard className="w-3.5 h-3.5" /> Panel Admin
              </Link>
            )}

            <Button variant="ghost" size="sm" className="relative" onClick={() => { setNotifOpen(true); fetchNotifications(); }} data-testid="notifications-btn">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{user?.name?.charAt(0) || 'U'}</span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  <ChevronRight className="w-3 h-3 rotate-90" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-xs text-slate-500">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-xs uppercase tracking-wider font-medium">{user?.role}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} className="text-xs cursor-pointer" data-testid="dropdown-change-password">
                  <KeyRound className="w-3 h-3 mr-2" /> Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="dropdown-logout">
                  <LogOut className="w-3 h-3 mr-2" /> Cerrar Sesion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setSidebarOpen(false)}>
            <aside className="w-64 bg-white h-full shadow-xl animate-slide-in flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <span className="heading-industrial text-lg font-bold text-slate-900">{companyName || "PANEL"}</span>
                <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-slate-100 rounded-sm" data-testid="mobile-sidebar-close">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="admin-mobile-nav">
                {isEmployee ? (
                  navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={navLinkClass(isActive(item.path))}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))
                ) : (
                  <>
                    {/* Buscador mobile */}
                    <div className="pb-3 sticky top-0 bg-white z-10">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        <input
                          type="text"
                          data-testid="admin-mobile-nav-search"
                          value={navSearch}
                          onChange={(e) => setNavSearch(e.target.value)}
                          placeholder="Buscar módulo..."
                          className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-slate-50"
                        />
                      </div>
                    </div>
                    {/* Favoritos mobile */}
                    {!navSearch && favoriteItems.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-amber-600 px-3 pt-2 pb-1.5 select-none flex items-center gap-1">
                          <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" /> Mis accesos
                        </p>
                        {favoriteItems.map((item) => (
                          <Link key={`mfav-${item.path}`} to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`mobile-nav-fav-${slugTestId(item.label)}`}
                            className={navLinkClass(isActive(item.path))}>
                            <item.icon className="w-4 h-4" /> {item.label}
                          </Link>
                        ))}
                        <div className="h-px bg-slate-100 mt-3 mb-1" />
                      </div>
                    )}
                    {searchedGroups.map((group) => {
                      const isCollapsed = !navSearch && !!collapsedGroups[group.label];
                      return (
                        <div key={group.label} className="mb-2">
                          <button type="button"
                            onClick={() => !navSearch && toggleGroup(group.label)}
                            disabled={!!navSearch}
                            className="w-full flex items-center gap-1.5 px-3 pt-3 pb-1.5 text-[9px] uppercase tracking-[0.15em] font-bold text-slate-400 hover:text-slate-600 select-none transition-colors">
                            <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                            {group.label}
                          </button>
                          {!isCollapsed && group.items.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`mobile-nav-${slugTestId(item.label)}`}
                              className={navLinkClass(isActive(item.path))}
                            >
                              <item.icon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </>
                )}
              </nav>
              <div className="p-4 border-t border-slate-200">
                <div className="flex items-center gap-2 px-3 py-2 mb-2">
                  <div className="w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{user?.name?.charAt(0) || 'U'}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
                  </div>
                </div>
                <button onClick={handleLogout} data-testid="mobile-logout-btn"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-red-600 hover:bg-red-50 w-full transition-colors">
                  <LogOut className="w-4 h-4" /> Cerrar Sesion
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-6 animate-fade-in">
          {isEmployee ? <Outlet /> : <RequireModule><Outlet /></RequireModule>}
        </main>

        {/* Notifications Dialog */}
        <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
          <DialogContent className="max-w-md rounded-sm max-h-[70vh] overflow-y-auto" data-testid="notifications-dialog">
            <DialogHeader>
              <DialogTitle className="heading-industrial text-lg uppercase flex items-center justify-between">
                Notificaciones
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-slate-500" data-testid="mark-all-read">
                    Marcar todas leidas
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              {notifications.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6" data-testid="notif-empty">No hay notificaciones pendientes</p>
              ) : (
                notifications.map(n => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleNotificationClick(n)}
                    data-testid={`notif-${n.id}`}
                    className="w-full text-left p-3 rounded-sm border bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-start gap-2">
                      {n.type === "cancelacion_cliente" && <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
                      {n.type === "modificacion_cliente" && <CalendarDays className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />}
                      {n.type === "portal_request" && <UserCog className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900">
                          {n.type === "cancelacion_cliente"
                            ? "Cancelación de cita"
                            : n.type === "modificacion_cliente"
                              ? "Solicitud de modificación"
                              : n.type === "portal_request"
                                ? `Portal Empleado · ${n.request_type || "Solicitud"}`
                                : "Notificación"}
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("es-ES")}</p>
                        <p className="text-[9px] text-amber-700 mt-1 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider font-semibold">
                          Click para revisar →
                        </p>
                      </div>
                      <Badge className="bg-red-100 text-red-700 text-[9px] rounded-sm border-red-200">Nueva</Badge>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="border-t border-slate-200 pt-3 mt-2">
              <button
                type="button"
                onClick={() => { setNotifOpen(false); navigate("/admin/notificaciones"); }}
                className="w-full text-center text-xs uppercase tracking-wider text-slate-600 hover:text-slate-900 font-semibold py-2 hover:bg-slate-50 rounded-sm transition-colors"
                data-testid="notif-view-all-btn"
              >
                Ver todas las notificaciones →
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
